import keys from '@/config/keys'
import store from '@/redux/store'
import endpoints from '@/config/endpoints'
import { getCspNonce } from '@/utils/cspNonce'
import getTerminology from '@/utils/getTerminology'
import { getAppRequestHeaders } from '@/utils/appRequestHeaders'
import { buildEmmaRequestHeaders } from '@/utils/buildEmmaRequestHeaders'
import { presentWafCaptcha, WAF_FALLBACK_ERROR_MESSAGE } from '@/utils/wafCaptchaPresenter'

const WAF_CHALLENGE_STATUS = 202
const WAF_CAPTCHA_FALLBACK_STATUSES = [405]
const WAF_SUCCESS_STATUS = 204
const SCRIPT_LOAD_TIMEOUT = 15000
const SCRIPT_READY_POLL_INTERVAL = 50
const WAF_CAPTCHA_ELEMENT = 'awswaf-captcha'

let scriptPromise = null
let grantPromise = null

const getHeader = (headers, name) => {
	if (!headers) return undefined
	if (typeof headers.get === 'function') return headers.get(name) ?? undefined

	const lowerName = name.toLowerCase()
	const headerKey = Object.keys(headers).find((key) => key.toLowerCase() === lowerName)
	return headerKey ? headers[headerKey] : undefined
}

const hasWafApi = () => {
	if (typeof window === 'undefined') return false

	return (
		typeof window.AwsWafIntegration?.fetch === 'function' &&
		typeof window.AwsWafCaptcha?.renderCaptcha === 'function'
	)
}

const getWafConfig = () => {
	const config = store.getState()?.settings?.waf_challenge
	if (!config || typeof config !== 'object' || config.enabled !== true) return null

	return {
		jsapi_url: typeof config.jsapi_url === 'string' ? config.jsapi_url.trim() : '',
		api_key: typeof config.api_key === 'string' ? config.api_key.trim() : '',
	}
}

export const getWafErrorMessage = () => {
	return getTerminology('waf_verification_failed', null, false, true) || WAF_FALLBACK_ERROR_MESSAGE
}

export const createWafError = (cause) => {
	const error = new Error(getWafErrorMessage())
	error.name = 'WafVerificationError'
	if (cause) error.cause = cause
	return error
}

const createLoggedWafError = (step, { response, cause } = {}) => {
	const sourceError = cause?.cause || cause
	const details = { step }
	const action = getHeader(response?.headers, keys.HEADERS.WAF_ACTION)

	if (typeof response?.status === 'number') details.status = response.status
	if (response?.statusText) details.statusText = response.statusText
	if (action) details.action = action
	if (sourceError?.name) details.errorName = sourceError.name
	if (sourceError?.code) details.errorCode = sourceError.code
	if (sourceError?.kind) details.errorKind = sourceError.kind
	if (sourceError?.message) details.errorMessage = sourceError.message

	// Keep the guest message simple, but show the failed step to developers.
	console.error('[AWS WAF] Verification failed', details)
	return createWafError(cause)
}

const needsWafCaptcha = (response) => {
	// Add statuses here when WAF asks for CAPTCHA without the challenge header.
	if (WAF_CAPTCHA_FALLBACK_STATUSES.includes(response?.status)) return true

	const action = getHeader(response?.headers, keys.HEADERS.WAF_ACTION)
	return (
		response?.status === WAF_CHALLENGE_STATUS &&
		typeof action === 'string' &&
		action.trim().toLowerCase() === 'challenge'
	)
}

const getUrlPath = (url, baseURL) => {
	if (typeof url !== 'string' || !url.trim()) return ''

	try {
		const baseUrl = new URL(baseURL || '/', window.location.origin)
		return new URL(url, baseUrl).pathname.replace(/\/+$/, '')
	} catch {
		return ''
	}
}

const WAF_AUTH_SCOPE = Object.freeze({
	ANY: 'any',
	GUEST: 'guest',
	AUTHENTICATED: 'authenticated',
})

// Add one row here when another API request needs WAF protection.
const WAF_REQUEST_RULES = [
	{
		endpoint: endpoints.register,
		methods: ['POST'],
		auth: WAF_AUTH_SCOPE.ANY,
	},
	{
		endpoint: endpoints.withSuffix('/checkout'),
		methods: ['PATCH'],
		auth: WAF_AUTH_SCOPE.GUEST,
	},
]

const WAF_AUTH_MATCHERS = {
	[WAF_AUTH_SCOPE.ANY]: () => true,
	[WAF_AUTH_SCOPE.GUEST]: (isAuthenticated) => !isAuthenticated,
	[WAF_AUTH_SCOPE.AUTHENTICATED]: (isAuthenticated) => isAuthenticated,
}

export const shouldValidateWafRequest = (config) => {
	if (!getWafConfig()) return false

	const method = config?.method?.toUpperCase()
	const path = getUrlPath(config?.url, config?.baseURL)
	const isAuthenticated = store.getState()?.auth?.isAuthenticated === true

	return WAF_REQUEST_RULES.some((rule) => {
		const matchesAuth = WAF_AUTH_MATCHERS[rule.auth]
		return (
			getUrlPath(rule.endpoint) === path &&
			rule.methods.includes(method) &&
			matchesAuth?.(isAuthenticated) === true
		)
	})
}

export const loadWafScript = async (url) => {
	if (hasWafApi()) return
	if (scriptPromise) return scriptPromise

	const scriptUrl = typeof url === 'string' ? url.trim() : ''
	if (!scriptUrl) {
		throw createLoggedWafError('script_config', {
			cause: new Error('The AWS WAF jsapi_url is missing.'),
		})
	}

	scriptPromise = new Promise((resolve, reject) => {
		const nonce = getCspNonce()?.trim()
		let timeoutId
		let pollId
		let settled = false
		let newScript = null

		const finish = (error) => {
			if (settled) return
			settled = true
			window.clearTimeout(timeoutId)
			window.clearTimeout(pollId)
			if (newScript) newScript.onerror = null

			if (error) {
				reject(createLoggedWafError('script_load', { cause: error }))
				return
			}

			resolve()
		}

		const waitUntilReady = () => {
			if (settled) return

			if (hasWafApi()) {
				finish()
				return
			}

			pollId = window.setTimeout(waitUntilReady, SCRIPT_READY_POLL_INTERVAL)
		}

		const existingScript = Array.from(document.scripts).find(
			(script) => script.dataset.awsWafIntegration === 'true' || script.src === scriptUrl,
		)
		const captchaElementExists = Boolean(window.customElements?.get(WAF_CAPTCHA_ELEMENT))

		if (!existingScript && !captchaElementExists) {
			newScript = document.createElement('script')
			newScript.src = scriptUrl
			newScript.defer = true
			newScript.dataset.awsWafIntegration = 'true'
			if (nonce) {
				newScript.nonce = nonce
				newScript.setAttribute('nonce', nonce)
			}
			newScript.onerror = () => finish(new Error('AWS WAF script could not load.'))
			document.head.appendChild(newScript)
		}

		// jsapi.js loads challenge.js itself, so wait until both APIs are ready.
		timeoutId = window.setTimeout(
			() => finish(new Error('AWS WAF script timed out.')),
			SCRIPT_LOAD_TIMEOUT,
		)
		waitUntilReady()
	})

	return scriptPromise
}

const validateWaf = async (step) => {
	try {
		const eventId = store.getState()?.settings?.[keys.FIELDS.eventId]
		return await window.AwsWafIntegration.fetch(endpoints.withSuffix('/waf/validate'), {
			method: 'POST',
			credentials: 'include',
			headers: {
				...buildEmmaRequestHeaders({ eventId }),
				...getAppRequestHeaders(endpoints.withSuffix('/waf/validate')),
			},
		})
	} catch (error) {
		throw createLoggedWafError(step, { cause: error })
	}
}

const requestWafGrant = async () => {
	try {
		const config = getWafConfig()
		if (!config?.jsapi_url) {
			throw createLoggedWafError('config', {
				cause: new Error('AWS WAF is enabled but jsapi_url is missing.'),
			})
		}

		await loadWafScript(config.jsapi_url)

		let response = await validateWaf('validate_before_captcha')
		if (response?.status === WAF_SUCCESS_STATUS) return

		if (!needsWafCaptcha(response)) {
			throw createLoggedWafError('validate_before_captcha', { response })
		}

		if (!config.api_key) {
			throw createLoggedWafError('captcha_config', {
				response,
				cause: new Error('AWS WAF requested CAPTCHA but api_key is missing.'),
			})
		}

		try {
			await presentWafCaptcha({
				apiKey: config.api_key,
				errorMessage: getWafErrorMessage(),
			})
		} catch (error) {
			throw createLoggedWafError('captcha', { cause: error })
		}

		response = await validateWaf('validate_after_captcha')
		if (response?.status !== WAF_SUCCESS_STATUS) {
			throw createLoggedWafError('validate_after_captcha', { response })
		}
	} catch (error) {
		throw error?.name === 'WafVerificationError'
			? error
			: createLoggedWafError('unknown', { cause: error })
	}
}

/**
 * Share one check when several protected requests fail at the same time.
 */
export const ensureWafGrant = () => {
	if (!grantPromise) {
		grantPromise = requestWafGrant().finally(() => {
			grantPromise = null
		})
	}

	return grantPromise
}

export const preloadWafScript = () => {
	const config = getWafConfig()
	if (!config?.jsapi_url) return Promise.resolve()
	return loadWafScript(config.jsapi_url)
}
